// Enrichment settings (docs/data-sources-v2-spec.md §6, docs/ticket-journey.md):
// which data sources run, in what order, under which conditions, how the step
// ends and what happens when nothing is found, plus the extra moments the
// sources run again. The « Réglages de l'enrichissement » view of
// /connectors (?tab=workflow; the old /connectors?view=workflow still lands
// here). The draft is saved as a whole; « Revenir au défaut » deletes the
// account's workflow after a confirmation.
// API: GET /api/automation/ticket-workflow, GET …/options, PUT …
// { enrichment }, DELETE … (employees read only: the backend refuses writes).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
    ArrowDown, ArrowUp, ChevronRight, Plus, RotateCcw, Save, SlidersHorizontal, X,
} from 'lucide-react';
import {
    Badge, Banner, Card, ConfirmDialog, EmptyState, Field, Switch,
} from '../design/ui';
import { ChipToggle } from './DataSourcesCreate';
import { formatStamp } from './dataToolsModel';
import './TicketWorkflow.css';

const API = '/api/automation/ticket-workflow';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const emptyConditions = () => ({ channels: null, clients: null, carriers: null, only_if_missing_order: false, requires: null });

const TicketWorkflow = ({ onFeedback, canEdit = true }) => {
    const { t } = useTranslation();
    const [workflow, setWorkflow] = useState(null);
    const [options, setOptions] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [wf, opts] = await Promise.all([axios.get(API, authHeaders()), axios.get(`${API}/options`, authHeaders())]);
            setWorkflow(wf.data);
            setDraft(JSON.parse(JSON.stringify(wf.data.enrichment)));
            setOptions(opts.data);
            setDirty(false);
        } catch (e) {
            setError(t('ticketWorkflow.loadError', 'Impossible de charger le parcours du ticket.'));
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const update = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };
    const updateSource = (index, patch) => update({ sources: draft.sources.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
    const updateConditions = (index, patch) => updateSource(index, { conditions: { ...(draft.sources[index].conditions || emptyConditions()), ...patch } });
    const move = (index, delta) => {
        const next = [...draft.sources];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        update({ sources: next });
    };
    const removeSource = (index) => update({ sources: draft.sources.filter((_, i) => i !== index) });
    const addSource = (key) => { if (key && !draft.sources.some((s) => s.key === key)) update({ sources: [...draft.sources, { key, enabled: true, conditions: emptyConditions() }] }); };

    const save = async () => {
        setBusy('save');
        setError(null);
        try {
            const res = await axios.put(API, { enrichment: draft }, authHeaders());
            setWorkflow(res.data);
            setDraft(JSON.parse(JSON.stringify(res.data.enrichment)));
            setDirty(false);
            onFeedback?.({ type: 'success', message: t('ticketWorkflow.saved', 'Parcours enregistré.') });
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : t('ticketWorkflow.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const reset = async () => {
        setBusy('reset');
        try {
            await axios.delete(API, authHeaders());
            setConfirmReset(false);
            await load();
            onFeedback?.({ type: 'success', message: t('ticketWorkflow.resetDone', 'Parcours remis au comportement par défaut.') });
        } catch (e) {
            setConfirmReset(false);
            setError(t('ticketWorkflow.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const sourceTitles = useMemo(() => Object.fromEntries((options?.sources || []).map((s) => [s.key, s.title])), [options]);
    const unlisted = useMemo(() => (options?.sources || []).filter((s) => !draft?.sources.some((d) => d.key === s.key)), [options, draft]);

    if (!draft || !options) {
        return error ? (
            <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={load}>{t('dataToolsUi.retry', 'Réessayer')}</button>}>{error}</Banner>
        ) : (
            <div className="tw-skeleton" aria-busy="true" aria-label={t('ticketWorkflow.loading', 'Chargement…')}>
                {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
            </div>
        );
    }

    const alsoOn = draft.also_on || {};

    return (
        <Card
            icon={SlidersHorizontal}
            title={t('ticketWorkflow.settingsTitle', 'Réglages de l’enrichissement')}
            badge={workflow?.persisted
                ? <Badge tone="accent">{t('dataToolsUi.workflow.custom', 'Personnalisé')}</Badge>
                : <Badge>{t('dataToolsUi.workflow.default', 'Par défaut')}</Badge>}
            description={t('ticketWorkflow.settingsDesc', 'Quelles sources lisent la commande, les colis et le suivi, dans quel ordre et sous quelles conditions. Le suivi des colis est en plus relu toutes les 12 h pendant 45 jours, indépendamment de ces réglages.')}
            footerNote={dirty
                ? t('dataToolsUi.workflow.unsaved', 'Modifications non enregistrées · elles s’appliqueront aux prochains tickets.')
                : t('dataToolsUi.workflow.footer', 'S’applique aux prochains tickets.')}
            footer={canEdit ? (
                <>
                    {workflow?.persisted && (
                        <button type="button" className="u-btn u-btn--secondary" disabled={busy === 'reset'} onClick={() => setConfirmReset(true)}>
                            <RotateCcw aria-hidden="true" /> {t('ticketWorkflow.reset', 'Revenir au défaut')}
                        </button>
                    )}
                    <button type="button" className="u-btn u-btn--primary" disabled={busy === 'save' || !dirty} onClick={save}>
                        <Save aria-hidden="true" /> {busy === 'save' ? t('common.saving', 'Enregistrement...') : t('ticketWorkflow.save', 'Enregistrer')}
                    </button>
                </>
            ) : null}
        >
            <div className="tw-body">
                {error && <Banner tone="danger">{error}</Banner>}
                <Banner tone="neutral">
                    {workflow?.persisted
                        ? [
                            t('ticketWorkflow.persisted', 'Parcours personnalisé, modifié le {{at}}', { at: workflow.updated_at ? formatStamp(workflow.updated_at) : '—' }),
                            workflow.updated_by ? t('dataToolsUi.workflow.by', 'par {{who}}', { who: workflow.updated_by }) : null,
                        ].filter(Boolean).join(' ')
                        : t('ticketWorkflow.implicitV2', 'Réglage par défaut : toutes vos sources, dans l’ordre de leur création, la première valeur trouvée l’emporte. Enregistrez pour le personnaliser.')}
                </Banner>

                <section className="tw-section">
                    <h3 className="tw-section__title">{t('dataToolsUi.workflow.sourcesTitle', 'Sources lues, dans l’ordre')}</h3>
                    {options.sources.length === 0 ? (
                        <EmptyState title={t('dataToolsUi.workflow.noSourceTitle', 'Aucune source de données configurée')}>
                            {t('ticketWorkflow.noSourcesConfigured', 'Aucune source de données configurée : créez-en une sur la page Sources de données.')}
                        </EmptyState>
                    ) : (
                        <div className="tw-sources">
                            {draft.sources.length === 0 && <p className="u-help tw-empty">{t('ticketWorkflow.noSources', 'Aucune source dans cette étape : les tickets ne sont pas enrichis.')}</p>}
                            {draft.sources.length > 0 && (
                                <ol className="u-list tw-list">
                                    {draft.sources.map((source, index) => (
                                        <SourceRow key={source.key} index={index} source={source} title={sourceTitles[source.key] || source.key}
                                            known={Boolean(sourceTitles[source.key])} options={options} t={t} canEdit={canEdit}
                                            onMove={move} onRemove={removeSource} onUpdate={updateSource} onConditions={updateConditions} total={draft.sources.length} />
                                    ))}
                                </ol>
                            )}
                            {canEdit && unlisted.length > 0 && (
                                <div className="tw-add">
                                    <Plus aria-hidden="true" />
                                    <select className="u-select" value="" onChange={(e) => addSource(e.target.value)} aria-label={t('ticketWorkflow.addSource', 'Ajouter une source à l’étape…')}>
                                        <option value="">{t('ticketWorkflow.addSource', 'Ajouter une source à l’étape…')}</option>
                                        {unlisted.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>
                    )}
                </section>

                <section className="tw-section">
                    <h3 className="tw-section__title">{t('dataToolsUi.workflow.behaviourTitle', 'Comportement de l’étape')}</h3>
                    <div className="u-form-grid tw-grid">
                        <Field label={t('ticketWorkflow.strategy', 'Comportement')} htmlFor="tw-strategy">
                            <select id="tw-strategy" className="u-select" value={draft.strategy} disabled={!canEdit} onChange={(e) => update({ strategy: e.target.value })}>
                                <option value="first_found">{t('ticketWorkflow.strategy_first_found', 'S’arrêter à la première source qui trouve')}</option>
                                <option value="merge">{t('ticketWorkflow.strategy_merge', 'Interroger toutes les sources et fusionner (la première valeur gagne)')}</option>
                            </select>
                        </Field>
                        <Field label={t('ticketWorkflow.onNotFound', 'Si rien n’est trouvé')} htmlFor="tw-not-found">
                            <select id="tw-not-found" className="u-select" value={draft.on_not_found} disabled={!canEdit} onChange={(e) => update({ on_not_found: e.target.value })}>
                                <option value="continue">{t('ticketWorkflow.onNotFound_continue', 'Continuer normalement')}</option>
                                <option value="flag_missing_info">{t('ticketWorkflow.onNotFound_flag', 'Signaler le ticket à l’équipe (informations manquantes)')}</option>
                                <option value="ask_customer">{t('ticketWorkflow.onNotFound_ask', 'Demander l’identifiant au client')}</option>
                            </select>
                        </Field>
                        <Field label={t('ticketWorkflow.timeout', 'Délai maximal de l’étape (s)')} htmlFor="tw-timeout"
                            help={t('dataToolsUi.workflow.timeoutHelp', 'Entre {{min}} et {{max}} secondes.', { min: options.timeout_bounds[0], max: options.timeout_bounds[1] })}>
                            <input id="tw-timeout" className="u-input tw-number" type="number" min={options.timeout_bounds[0]} max={options.timeout_bounds[1]} value={draft.timeout_s} disabled={!canEdit}
                                onChange={(e) => update({ timeout_s: parseInt(e.target.value, 10) || options.timeout_bounds[0] })} />
                        </Field>
                        <div className="tw-switch-cell">
                            <Switch id="tw-unlisted" checked={draft.include_unlisted !== false} disabled={!canEdit} onChange={(on) => update({ include_unlisted: on })}
                                label={t('ticketWorkflow.includeUnlisted', 'Les sources créées plus tard rejoignent l’étape en fin de liste')} />
                        </div>
                    </div>
                </section>

                <section className="tw-section">
                    <h3 className="tw-section__title">{t('ticketWorkflow.alsoOn', 'Enrichir aussi quand…')}</h3>
                    <div className="tw-also">
                        <Switch id="tw-also-message" checked={Boolean(alsoOn.new_customer_message)} disabled={!canEdit}
                            onChange={(on) => update({ also_on: { ...alsoOn, new_customer_message: on } })} label={t('ticketWorkflow.also_newMessage', 'le client envoie un nouveau message')} />
                        <Switch id="tw-also-tool" checked={Boolean(alsoOn.agent_tool)} disabled={!canEdit}
                            onChange={(on) => update({ also_on: { ...alsoOn, agent_tool: on } })} label={t('ticketWorkflow.also_agentTool', 'l’agent le demande pendant sa réponse (outil)')} />
                        <div className="tw-also__schedule">
                            <Switch id="tw-also-schedule" checked={Boolean(alsoOn.scheduled_every_minutes)} disabled={!canEdit}
                                onChange={(on) => update({ also_on: { ...alsoOn, scheduled_every_minutes: on ? 360 : null } })} label={t('ticketWorkflow.also_scheduled', 'toutes les')} />
                            <input className="u-input tw-number" type="number" min={options.min_schedule_minutes} disabled={!canEdit || !alsoOn.scheduled_every_minutes}
                                aria-label={t('dataToolsUi.workflow.everyMinutes', 'Intervalle en minutes')}
                                value={alsoOn.scheduled_every_minutes || ''} onChange={(e) => update({ also_on: { ...alsoOn, scheduled_every_minutes: parseInt(e.target.value, 10) || options.min_schedule_minutes } })} />
                            <span className="u-muted">{t('ticketWorkflow.also_minutes', 'minutes pour les tickets ouverts')}</span>
                        </div>
                    </div>
                </section>
            </div>

            {confirmReset && (
                <ConfirmDialog
                    icon={RotateCcw}
                    title={t('dataToolsUi.workflow.resetTitle', 'Revenir aux réglages par défaut ?')}
                    message={t('dataToolsUi.workflow.resetMessage', 'Votre ordre des sources et leurs conditions sont supprimés : toutes vos sources sont lues dans l’ordre de leur création, la première valeur trouvée l’emporte.')}
                    confirmLabel={t('ticketWorkflow.reset', 'Revenir au défaut')}
                    cancelLabel={t('common.cancel', 'Annuler')}
                    busy={busy === 'reset'}
                    onConfirm={reset}
                    onCancel={() => setConfirmReset(false)}
                />
            )}
        </Card>
    );
};

const ChipSelect = ({ label, values, selected, onChange, t, labelOf, disabled }) => {
    const current = selected || [];
    const toggle = (value) => {
        const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        onChange(next.length ? next : null);
    };
    return (
        <div className="tw-cond">
            <span className="tw-cond__label">
                {label}
                {!current.length && <span className="u-muted"> {t('ticketWorkflow.all', '(tous)')}</span>}
            </span>
            <div className="ds-chips">
                {values.map((value) => (
                    <ChipToggle key={value} checked={current.includes(value)} onChange={() => !disabled && toggle(value)}>
                        {labelOf ? labelOf(value) : value}
                    </ChipToggle>
                ))}
            </div>
        </div>
    );
};

const summarizeConditions = (conditions, t) => {
    const parts = [];
    if (conditions.channels?.length) parts.push(`${t('ticketWorkflow.channels', 'Canaux')} : ${conditions.channels.length}`);
    if (conditions.clients?.length) parts.push(`${t('ticketWorkflow.clients', 'Clients B2B')} : ${conditions.clients.length}`);
    if (conditions.carriers?.length) parts.push(`${t('ticketWorkflow.carriers', 'Transporteurs')} : ${conditions.carriers.length}`);
    if (conditions.requires?.length) parts.push(`${t('ticketWorkflow.requiresShort', 'identifiants')} : ${conditions.requires.map((v) => t(`ticketWorkflow.identifier.${v}`, v)).join(', ')}`);
    if (conditions.only_if_missing_order) parts.push(t('ticketWorkflow.onlyIfMissingOrderShort', 'sans commande'));
    return parts.length ? parts.join(' · ') : t('ticketWorkflow.always', 'toujours');
};

const SourceRow = ({ index, total, source, title, known, options, t, canEdit, onMove, onRemove, onUpdate, onConditions }) => {
    const conditions = source.conditions || emptyConditions();
    const channelLabel = (c) => t(`ticketWorkflow.channel.${c}`, c);
    const off = source.enabled === false;
    return (
        <li className={`tw-source${off ? ' is-off' : ''}`}>
            <div className="u-row tw-source__row">
                <span className="tw-order" aria-hidden="true">{index + 1}</span>
                <div className="u-row__main">
                    <div className="u-row__title">
                        {title}
                        {!known && <Badge tone="warning" dot>{t('ticketWorkflow.unknownSource', 'source introuvable')}</Badge>}
                        {off && <Badge>{t('dataToolsUi.workflow.off', 'désactivée')}</Badge>}
                    </div>
                    <div className="u-row__meta" title={summarizeConditions(conditions, t)}>
                        {t('ticketWorkflow.conditions', 'Conditions')} : {summarizeConditions(conditions, t)}
                    </div>
                </div>
                <div className="u-row__side">
                    <Switch id={`tw-on-${source.key}`} checked={!off} disabled={!canEdit} onChange={(on) => onUpdate(index, { enabled: on })}
                        label={<span className="tw-visually-hidden">{t('ticketWorkflow.enabled', 'active')}</span>} />
                    {canEdit && (
                        <>
                            <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" disabled={index === 0} onClick={() => onMove(index, -1)}
                                title={t('ticketWorkflow.moveUp', 'Monter')} aria-label={t('ticketWorkflow.moveUp', 'Monter')}>
                                <ArrowUp aria-hidden="true" />
                            </button>
                            <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" disabled={index === total - 1} onClick={() => onMove(index, 1)}
                                title={t('ticketWorkflow.moveDown', 'Descendre')} aria-label={t('ticketWorkflow.moveDown', 'Descendre')}>
                                <ArrowDown aria-hidden="true" />
                            </button>
                            <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={() => onRemove(index)}
                                title={t('ticketWorkflow.remove', 'Retirer de l’étape')} aria-label={t('ticketWorkflow.remove', 'Retirer de l’étape')}>
                                <X aria-hidden="true" />
                            </button>
                        </>
                    )}
                </div>
            </div>
            <details className="u-disclosure tw-conditions">
                <summary><ChevronRight aria-hidden="true" />{t('dataToolsUi.workflow.editConditions', 'Conditions de lecture')}</summary>
                <div className="tw-conditions__body">
                    <ChipSelect label={t('ticketWorkflow.channels', 'Canaux')} values={options.channels} selected={conditions.channels} disabled={!canEdit}
                        onChange={(v) => onConditions(index, { channels: v })} t={t} labelOf={channelLabel} />
                    {options.clients.length > 0 && (
                        <ChipSelect label={t('ticketWorkflow.clients', 'Clients B2B')} values={options.clients.map((c) => c.name)} selected={conditions.clients} disabled={!canEdit}
                            onChange={(v) => onConditions(index, { clients: v })} t={t} />
                    )}
                    {options.carriers.length > 0 && (
                        <ChipSelect label={t('ticketWorkflow.carriers', 'Transporteurs')} values={options.carriers} selected={conditions.carriers} disabled={!canEdit}
                            onChange={(v) => onConditions(index, { carriers: v })} t={t} />
                    )}
                    <ChipSelect label={t('ticketWorkflow.requires', 'Seulement si le ticket porte')} values={options.identifiers} selected={conditions.requires} disabled={!canEdit}
                        onChange={(v) => onConditions(index, { requires: v })} t={t} labelOf={(v) => t(`ticketWorkflow.identifier.${v}`, v)} />
                    <Switch id={`tw-missing-${source.key}`} checked={Boolean(conditions.only_if_missing_order)} disabled={!canEdit}
                        onChange={(on) => onConditions(index, { only_if_missing_order: on })} label={t('ticketWorkflow.onlyIfMissingOrder', 'Seulement si le ticket n’a pas encore de commande')} />
                </div>
            </details>
        </li>
    );
};

export default TicketWorkflow;
